import { useState } from 'react';
import { ApiError, request } from '../../../api';
import { useResource } from '../../../shell/hooks';
import { Button, Card, DataTable, Moment, Notice, Resourceful, TextInput, type Column } from '../../../shell/kit';
import { useToast } from '../../../shell/toast';
import type { IdentityTranslate } from '../i18n';

/**
 * Сроки хранения персональных данных (ТЗ §8.4.3). Срок назначает оператор ПДн;
 * очищают данные задачи модулей по расписанию, здесь виден последний прогон.
 */

interface RetentionRule {
  readonly category: string;
  readonly owner: string;
  readonly days: number;
  readonly description: string;
  readonly lastRunAt: string | null;
  readonly lastAffected: number | null;
}

export function RetentionView({ t }: { t: IdentityTranslate }) {
  const toast = useToast();
  const rules = useResource(
    () => request<{ rules: readonly RetentionRule[] }>('GET', '/api/v1/privacy/retention'),
    [],
  );
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const save = async (rule: RetentionRule): Promise<void> => {
    const days = Number(draft[rule.category] ?? rule.days);
    setBusy(true);
    try {
      await request('PATCH', `/api/v1/privacy/retention/${encodeURIComponent(rule.category)}`, { days });
      toast.ok(t('identity.retention.saved'));
      setDraft((prev) => {
        const next = { ...prev };
        delete next[rule.category];
        return next;
      });
      rules.reload();
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.message, err.code);
      else toast.error(String((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const columns: ReadonlyArray<Column<RetentionRule>> = [
    { key: 'what', header: t('identity.retention.what'), render: (r) => r.description },
    { key: 'owner', header: t('identity.retention.owner'), render: (r) => <code className="text-xs">{r.owner}</code> },
    {
      key: 'days',
      header: t('identity.retention.days'),
      render: (r) => (
        <div className="flex items-center gap-2">
          <TextInput
            type="number"
            min={1}
            max={3650}
            className="w-24"
            aria-label={t('identity.retention.days')}
            value={draft[r.category] ?? String(r.days)}
            onChange={(e) => setDraft((prev) => ({ ...prev, [r.category]: e.target.value }))}
          />
          {draft[r.category] !== undefined && draft[r.category] !== String(r.days) ? (
            <Button size="sm" tone="primary" disabled={busy} onClick={() => void save(r)}>
              {t('identity.card.save')}
            </Button>
          ) : null}
        </div>
      ),
    },
    {
      key: 'last',
      header: t('identity.retention.lastRun'),
      render: (r) =>
        r.lastRunAt ? (
          <span>
            <Moment iso={r.lastRunAt} /> · {t('identity.retention.affected', { n: r.lastAffected ?? 0 })}
          </span>
        ) : (
          <span className="text-ink-muted">{t('identity.retention.never')}</span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Notice tone="info">{t('identity.retention.hint')}</Notice>
      <Card title={t('identity.tab.retention')}>
        <Resourceful resource={rules}>
          {(data) => <DataTable rows={data.rules} columns={columns} rowKey={(r) => r.category} empty="—" />}
        </Resourceful>
      </Card>
    </div>
  );
}
